import { useEffect, useState } from "react";
import { Calendar, Clock, MapPin, X } from "lucide-react";

const URL_RE = /(https?:\/\/[^\s<>()\[\]"']+)/g;

const linkify = (text) =>
    text.split(URL_RE).map((part, i) =>
        URL_RE.test(part) ? (
            <a key={i} href={part} target="_blank" rel="noreferrer" className="font-semibold text-forest underline decoration-2 underline-offset-2 hover:text-brick">
                {part.length > 48 ? part.slice(0, 45) + "…" : part}
            </a>
        ) : (
            part
        ),
    );

const EventModal = ({ event, onClose }) => {
    const [imgFailed, setImgFailed] = useState(false);
    useEffect(() => {
        setImgFailed(false);
    }, [event?.id]);
    useEffect(() => {
        if (!event) return;
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [event, onClose]);

    if (!event) return null;

    return (
        <div
            data-testid="event-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 p-4"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={event.title}
        >
            <div className="stamp-frame max-h-[90vh] w-full max-w-lg overflow-y-auto bg-paper p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-4">
                    <h3 data-testid="event-modal-title" className="font-display text-3xl leading-tight tracking-wide text-ink sm:text-4xl">
                        {event.title}
                    </h3>
                    <button onClick={onClose} aria-label="Close event details" data-testid="event-modal-close" className="border-2 border-ink bg-cream p-1.5 hard-shadow transition-transform duration-150 hover:-translate-y-0.5">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <ul className="mt-4 space-y-2 text-sm font-semibold text-ink/80">
                    <li className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 shrink-0 text-orange" /> <span data-testid="event-modal-date">{event.date}</span>
                    </li>
                    {event.time ? (
                        <li className="flex items-center gap-2">
                            <Clock className="h-4 w-4 shrink-0 text-orange" /> <span data-testid="event-modal-time">{event.time} CT</span>
                        </li>
                    ) : (
                        <li className="flex items-center gap-2">
                            <Clock className="h-4 w-4 shrink-0 text-orange" /> <span>All day</span>
                        </li>
                    )}
                    {event.location ? (
                        <li className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 shrink-0 text-orange" /> <span data-testid="event-modal-location">{event.location}</span>
                        </li>
                    ) : null}
                </ul>
                {event.image && !imgFailed ? (
                    <div className="mt-4 -rotate-1 border-2 border-ink bg-cream p-2 hard-shadow">
                        <img
                            src={event.image}
                            alt={`${event.title} flyer`}
                            referrerPolicy="no-referrer"
                            data-testid="event-modal-image"
                            onError={() => setImgFailed(true)}
                            className="max-h-[28rem] w-full border-2 border-ink object-contain bg-paper"
                        />
                    </div>
                ) : null}
                {event.description ? (
                    <p data-testid="event-modal-description" className="mt-4 whitespace-pre-line text-sm leading-relaxed text-ink/75">
                        {linkify(event.description)}
                    </p>
                ) : null}
                {event.rsvp_url ? (
                    <a
                        href={event.rsvp_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="event-modal-link"
                        className="btn-stamp mt-6 inline-block !bg-orange"
                    >
                        Details / RSVP →
                    </a>
                ) : null}
            </div>
        </div>
    );
};

export default EventModal;
